"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.estimateProcessingSeconds = exports.createHistoricEtsPolygons = exports.createEtsPolygons = exports.createEtsPolygonsInternal = void 0;
const turf_1 = require("@turf/turf");
const connection_graph_1 = require("./calculators/connection-graph");
const bridge_polygons_1 = require("./operations/bridge-polygons");
const fill_holes_1 = require("./operations/fill-holes");
const simplify_polygons_1 = require("./operations/simplify-polygons");
const classify_polygons_1 = require("./operations/classify-polygons");
const boundary_crop_1 = require("./operations/boundary-crop");
const filter_forest_polygons_1 = require("./operations/filter-forest-polygons");
const remove_small_1 = require("./operations/remove-small");
const difference_1 = require("./operations/difference");
const geojson_converters_1 = require("./geojson-converters");
const remove_overlap_1 = require("./operations/remove-overlap");
const cluster_graph_1 = require("./calculators/cluster-graph");
/**
 * @constant
 *
 * Any hole of smaller area than this will be filled.
 */
const MIN_HOLE_AREA_SQUARE_METERS = (0, turf_1.convertArea)(1, 'hectares', 'meters');
/**
 * @constant
 *
 * Any feature that (at the end of processing) has an area less than this
 * will be removed
 */
const MIN_FEATURE_AREA_SQUARE_METERS = (0, turf_1.convertArea)(0.05, 'hectares', 'meters');
/**
 * @constant
 * The maximum distance to consider two polygons "near" each other and possible
 * to connect.
 * The smaller this is, the more efficient the algorithm will be (O(n) -> O(n2)),
 * but it needs to be large enough that we aren't missing things from the ETS rules
 * because of it. The ETS rules state you can join a max of 15m out from each
 * forest, for a total bridging distance of 30m. However most people assume this
 * to be a max of 15m bridging, so we'll use 15m here.
 * The algorithm may return edges with lengths greater than this, but it will not
 * pick up all cases where they are.
 */
const MAX_BRIDGE_LENGTH_METERS = 15;
/**
 * @constant
 * How wide to make the initial bridge between features
 */
const APPROX_BRIDGE_WIDTH_METERS = 0.1;
/**
 * @constant
 * The min feature area (A & B) the connection graph should allow in square meters
 */
const MAX_BRIDGE_AREA = 10000;
/**
 * @constant
 * The maximum length of a single edge of an output polygon
 */
const MAX_EDGE_LENGTH_METERS = 15;
/**
 * @constant
 * Ensure that all polygons are at least this distance away from the borders of the property
 */
const LAND_BOUNDARY_BUFFER_METERS = 0.1;
/**
 * @constant
 * Ensure that all there are no tentacles caused by differencing historic boundaries
 */
const INELIGIBLE_FEATURES_BUFFER = 0.0001;
/**
 * @constant
 * Console log if simplified area becomes less needs to account for a small increase for tests
 */
const ALLOWED_REDUCED_SIMPLIFIED_AREA_SQM = 0.000001;
/**
 * Converts from the output of the AI polygonization into a format that can be worked
 * with for computing ETS parameters
 *
 * @param aiFeatures Features representing detailed information about some land
 * @returns Features containing detailed information about some land - with extra properties
 */
function aiFeatureCollectionToEtsFeatureCollection(aiFeatures) {
    return (0, turf_1.featureCollection)(aiFeatures.features.map((feature) => {
        var _a;
        return ({
            ...feature,
            properties: {
                report_category: undefined,
                canopyCoverProportion: 1.0,
                ets_species: ((_a = feature.properties.class_labels) === null || _a === void 0 ? void 0 : _a.includes('EXOTIC'))
                    ? 'pinus_radiata'
                    : 'indigenous',
                class: feature.properties.class,
            },
        });
    }));
}
/**
 * Simplifies a single set of ets features in compliance with the ETS mapping rules.
 * This can be a time consuming operation. If user interaction is required, consider
 * calling `estimateProcessingSeconds` so the user is informed beforehand.
 *
 * @param etsFeatures ETS Features to Output
 * @param titleBoundaries Features describing what land this project encompasses
 * @returns Simplified polygons that comply with the ETS mapping rules
 */
function createEtsPolygonsInternal(etsFeatures, titleBoundaries) {
    const initialArea = (0, turf_1.area)(etsFeatures);
    let graph = (0, connection_graph_1.computeGraph)(etsFeatures, MAX_BRIDGE_LENGTH_METERS);
    graph = (0, connection_graph_1.filterGraphByMinDistance)(graph, MAX_BRIDGE_LENGTH_METERS);
    graph = (0, connection_graph_1.filterGraphByMaxArea)(graph, MAX_BRIDGE_AREA);
    graph = (0, cluster_graph_1.computeSubGraph)(graph);
    // Note:
    // Bridge polygons currently uses the properties from a single feature, but
    // the graph may bridge between features with different properties.
    // Currently this isn't a problem because we don't attempt to set the report_category
    // property, but if we do in the future, we'll need to change this.
    const joinedPolygons = (0, bridge_polygons_1.bridgePolygons)(etsFeatures, graph, APPROX_BRIDGE_WIDTH_METERS);
    const joinedArea = (0, turf_1.area)(joinedPolygons);
    if (joinedArea < initialArea) {
        // eslint-disable-next-line no-console
        console.error(`Bridging did shrink total area by ${initialArea - joinedArea}sqm`);
    }
    const simplifiedPolygons = (0, simplify_polygons_1.simplifyFeatureCollection)(joinedPolygons, MAX_EDGE_LENGTH_METERS);
    const simplifiedArea = (0, turf_1.area)(simplifiedPolygons);
    if (joinedArea - simplifiedArea > ALLOWED_REDUCED_SIMPLIFIED_AREA_SQM) {
        // eslint-disable-next-line no-console
        console.error(`Simplifying did shrink total area by ${joinedArea - simplifiedArea}sqm`);
    }
    const filledHolesPolygons = (0, fill_holes_1.fillHoles)(simplifiedPolygons, MIN_HOLE_AREA_SQUARE_METERS);
    const noOverlappedPolygons = (0, remove_overlap_1.removeOverlap)(filledHolesPolygons);
    const croppedPolygons = (0, boundary_crop_1.cropToTitleBoundaries)(noOverlappedPolygons, titleBoundaries, LAND_BOUNDARY_BUFFER_METERS);
    const smallFeaturesRemovedPolygons = (0, remove_small_1.removeSmallPolygons)(croppedPolygons, MIN_FEATURE_AREA_SQUARE_METERS);
    return smallFeaturesRemovedPolygons;
}
exports.createEtsPolygonsInternal = createEtsPolygonsInternal;
/**
 * Simplifies a group of polygons in compliance with the ETS mapping rules.
 * This can be a time consuming operation. If user interaction is required, consider
 * calling `estimateProcessingSeconds` so the user is informed beforehand.
 *
 * @param aiFeatures Features from the AI
 * @param titleBoundaries Features describing what land this project encompasses
 * @returns Simplified polygons that comply with the ETS mapping rules
 */
function createEtsPolygons(aiFeatures, titleBoundaries) {
    const aiForestFeatures = (0, filter_forest_polygons_1.filterForestPolygons)((0, turf_1.rewind)(aiFeatures));
    const etsFeatures = aiFeatureCollectionToEtsFeatureCollection(aiForestFeatures);
    const etsPolygons = createEtsPolygonsInternal(etsFeatures, titleBoundaries);
    return (0, classify_polygons_1.classifyPolygons)(etsPolygons, classify_polygons_1.ClassifyBehavior.Legacy);
}
exports.createEtsPolygons = createEtsPolygons;
/**
 * Separates eligible forest from ineligible (pre-90) forest and simplifies the output
 * in compliance with the ETS mapping rules. This can be a time consuming operation.
 * If user interaction is required, consider calling `estimateProcessingSeconds` so
 * the user is informed beforehand.
 *
 * Flow of data and operations below:
 
  PRESENT DAY AI POLYGONS            HISTORIC AI POLYGONS
           │                                 │
           ├──────────────────────┐      ┌───┘
           │                      │      │
           │                 ┌────▼──────▼────┐
           │                 │                │
           │                 │   Difference   │
       ┌───▼────────────┐    │                │
       │                │    └────────┬───────┘
       │  ETS Polygons  │             │
       │                │    ┌────────▼───────┐
       └───────┬────────┘    │                │
               │          ┌──┤  ETS Polygons  │
       ┌───────▼────────┐ │  │                │
       │                │ │  └──┬─────────────┘
       │   Difference   ◄─┘     │
       │                │       │
       └────────────┬───┘       │
                    │           │
                 ┌──▼───────────▼─┐
                 │                │
                 │      ADD       │
                 │                │
                 └───────┬────────┘
                         │
                         ▼
                        OUT
 *
 * @param aiFeatures Present day features from the AI
 * @param historicAiFeatures Historic features from the AI
 * @param titleBoundaries Features describing what land this project encompasses
 * @returns Simplified polygons that comply with the ETS mapping rules
 */
function createHistoricEtsPolygons(aiFeatures, historicAiFeatures, titleBoundaries) {
    const aiForestFeatures = (0, filter_forest_polygons_1.filterForestPolygons)((0, turf_1.rewind)(aiFeatures));
    const etsFeatures = aiFeatureCollectionToEtsFeatureCollection(aiForestFeatures);
    const aiHistoricForestFeatures = (0, filter_forest_polygons_1.filterForestPolygons)((0, turf_1.rewind)(historicAiFeatures));
    const etsHistoricFeatures = aiFeatureCollectionToEtsFeatureCollection(aiHistoricForestFeatures);
    const aiEligible = (0, geojson_converters_1.flattenMultiFeatureCollection)((0, difference_1.featureCollectionDifference)(etsFeatures, etsHistoricFeatures));
    const etsPost89Features = (0, classify_polygons_1.classifyPolygons)(createEtsPolygonsInternal(aiEligible, titleBoundaries), classify_polygons_1.ClassifyBehavior.Post89);
    const etsPre90Features = (0, classify_polygons_1.classifyPolygons)((0, geojson_converters_1.flattenMultiFeatureCollection)((0, difference_1.featureCollectionDifference)((0, turf_1.buffer)(createEtsPolygons(aiFeatures, titleBoundaries), -INELIGIBLE_FEATURES_BUFFER), etsPost89Features)), classify_polygons_1.ClassifyBehavior.Pre90);
    const reportPolygons = (0, turf_1.featureCollection)([
        ...etsPost89Features.features,
        ...etsPre90Features.features,
    ]);
    return reportPolygons;
}
exports.createHistoricEtsPolygons = createHistoricEtsPolygons;
/**
 * Creating ETS polygons can be quite time consuming. This function estimates how long the
 * process will take.
 *
 * The internals of this function are opaque for the purposes of external callers (it has the
 * same input parameters as `createEtsPolygons`), but for developers it is handy to know that
 * it is a least-squares analysis that comes from analyzing existing projects.
 * It uses the filesize of the JSON as an approximation of complexity, and it uses the
 * area of the boundary as an approximation of feature density.
 *
 * @param aiFeatures Features from the AI
 * @param boundary Features describing what land this project encompasses
 * @returns The time (in seconds) that the `createEtsPolygons` function will take to execute.
 */
function estimateProcessingSeconds(aiFeatures, boundary) {
    const boundaryArea = (0, turf_1.convertArea)((0, turf_1.area)(boundary), 'meters', 'hectares');
    const ba2 = boundaryArea ** 2;
    const fileSizeKb = JSON.stringify(aiFeatures).length / 1000;
    const fs2 = fileSizeKb ** 2;
    const weights = [-2.8245, 2.6541e-2, -5.3335e-7, -3.5725e-1, 1.0468e-4]; // Found through least squares analysis of a bunch of projects
    return (weights[0] +
        weights[1] * fileSizeKb +
        weights[2] * fs2 +
        weights[3] * boundaryArea +
        weights[4] * ba2);
}
exports.estimateProcessingSeconds = estimateProcessingSeconds;
